import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatINR, productImg } from "@/lib/api";

const Star = ({ filled = true }) => <span style={{ color: filled ? "var(--bloom)" : "#F0C9D6" }}>★</span>;
const Stars5 = ({ rating = 5 }) => (
  <span className="rating-stars">{[1,2,3,4,5].map((i) => <Star key={i} filled={i <= Math.round(rating)} />)}</span>
);

export default function Home() {
  const [products, setProducts] = useState([]);
  const [shape, setShape] = useState(null);
  const [content, setContent] = useState(null);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/content").then(({ data }) => setContent(data));
  }, []);

  // Auto-rotate 3D pack showcase through all uploaded products
  useEffect(() => {
    if (products.length <= 1) return;
    const t = setInterval(() => setCarouselIdx((i) => (i + 1) % products.length), 2400);
    return () => clearInterval(t);
  }, [products.length]);

  if (!content) return <div className="min-h-screen" />;
  const c = content;
  const current = products[carouselIdx] || products[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        <div className="hero-orb" />
        <div className="max-w-[1180px] mx-auto px-6 grid lg:grid-cols-[1.05fr_.95fr] gap-10 items-center relative z-[2]">
          <div>
            <span className="eyebrow">{c.hero.badge}</span>
            <h1 className="text-[clamp(2.6rem,5.6vw,4.5rem)] mt-4 mb-2.5">
              {c.hero.headline_line1}<br />
              <em style={{ color: "var(--bloom)" }}>{c.hero.headline_italic}</em> {c.hero.headline_strike}
            </h1>
            <p className="text-[1.12rem] text-[var(--ink-soft)] max-w-[440px] mt-4">{c.hero.subtitle}</p>
            <div className="flex flex-wrap gap-3.5 mt-7">
              <Link to="/shop" data-testid="hero-shop-cta" className="btn btn-primary">{c.hero.primary_cta} ♡</Link>
              <Link to={c.hero.secondary_link || "/shop"} data-testid="hero-y2k-cta" className="btn btn-ghost">{c.hero.secondary_cta}</Link>
            </div>
            <div className="flex flex-wrap gap-6 mt-8">
              {[
                { v: c.hero.stat1_value, l: c.hero.stat1_label },
                { v: c.hero.stat2_value, l: c.hero.stat2_label },
                { v: c.hero.stat3_value, l: c.hero.stat3_label },
              ].map((s) => (
                <div key={s.l} className="text-[var(--ink-soft)] text-[.82rem] font-semibold flex items-center gap-2">
                  <b className="font-serif text-[1.4rem] text-[var(--ink)] font-semibold leading-none block">{s.v}</b>
                  <span>{s.l}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 3D pack stage */}
          <div className="relative h-[440px] grid place-items-center">
            <div className="float-orn" style={{ top: "8%", left: "6%" }}><div className="petal-shape" /></div>
            <div className="float-orn" style={{ bottom: "12%", right: "4%", animationDelay: "1.2s", transform: "scale(.7)" }}><div className="petal-shape" /></div>
            <div className="float-orn" style={{ top: "18%", right: "14%", animationDelay: ".6s", fontSize: "1.6rem" }}>✦</div>
            <div className="float-orn" style={{ bottom: "20%", left: "10%", animationDelay: "2s", fontSize: "1.6rem" }}>✿</div>

            <div className="relative w-[270px] h-[340px]" style={{ transformStyle: "preserve-3d", transform: "rotateX(8deg) rotateY(-16deg)" }}>
              <div className="absolute inset-0 rounded-[34px] p-7 flex flex-col overflow-hidden border border-white/70"
                style={{
                  background: "linear-gradient(160deg, #fff 0%, #FFE9F0 45%, var(--rose) 100%)",
                  boxShadow: "0 40px 70px -30px rgba(224,53,107,.55), inset 0 2px 6px rgba(255,255,255,.9)",
                }}>
                <span className="absolute top-5 right-5 tag-pill">16 pcs</span>
                <div className="font-serif text-[1.5rem] font-semibold tracking-tight">
                  Nailedit Beauty
                  <small className="block font-sans text-[.62rem] tracking-[.3em] font-bold uppercase mt-0.5" style={{ color: "var(--bloom)" }}>
                    gel nail stickers
                  </small>
                </div>
                <div className="mt-5 mx-auto relative w-[160px] h-[160px]" style={{ transform: "translateZ(40px)" }} data-testid="hero-3d-carousel">
                  {products.map((p, i) => (
                    <img
                      key={p.id}
                      src={productImg(p.images?.[0])}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full rounded-2xl object-cover shadow-md transition-all duration-700 ease-out"
                      style={{
                        opacity: i === carouselIdx ? 1 : 0,
                        transform: i === carouselIdx ? "scale(1) rotate(0deg)" : "scale(.92) rotate(-4deg)",
                      }}
                    />
                  ))}
                </div>
                <div className="mt-auto text-[.85rem] font-bold text-[var(--ink-soft)] truncate transition-opacity duration-500">
                  {current?.name || "New holo drop"}
                </div>
                {products.length > 1 && (
                  <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                    {products.slice(0, 8).map((_, i) => (
                      <span
                        key={i}
                        className="block h-1 rounded-full transition-all duration-500"
                        style={{
                          width: i === (carouselIdx % Math.min(products.length, 8)) ? 16 : 5,
                          background: i === (carouselIdx % Math.min(products.length, 8)) ? "var(--bloom)" : "rgba(224,53,107,.28)",
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="marquee-bar">
        <div className="marquee-track">
          {[0, 1].map((i) => (
            <span key={i}>
              {(c.marquee_top || []).map((t, j) => <span key={j}>{t} <b>·</b> </span>)}
            </span>
          ))}
        </div>
      </div>

      {/* SHOP GRID */}
      <section className="py-20" id="shop">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-[640px] mx-auto mb-12">
            <span className="eyebrow">{c.rail.eyebrow}</span>
            <h2 className="text-[clamp(2rem,4vw,3rem)] mt-3 mb-3">{c.rail.headline_part1} <em style={{ color: "var(--bloom)" }}>{c.rail.headline_italic}</em></h2>
          </div>

          <FeaturedCarousel products={products.filter((p) => p.featured)} />
        </div>
      </section>

      {/* KIDS COLLECTION */}
      {c.kids?.enabled && (
        <section className="py-20" id="kids" style={{ background: "var(--petal-2)" }}>
          <div className="max-w-[1180px] mx-auto px-6">
            <div className="text-center max-w-[640px] mx-auto mb-12">
              <span className="eyebrow">{c.kids.eyebrow}</span>
              <h2 className="text-[clamp(2rem,4vw,3rem)] mt-3 mb-3">
                {c.kids.headline_part1} <em style={{ color: "var(--bloom)" }}>{c.kids.headline_italic}</em>
              </h2>
              {c.kids.subtitle && <p className="text-[var(--ink-soft)]">{c.kids.subtitle}</p>}
            </div>
            <FeaturedCarousel
              products={products.filter((p) => (p.category || "").toLowerCase() === "kids")}
              emptyMessage="Dropping soon ♡ — join our list to be the first to know."
              seeAllHref="/shop?category=Kids"
            />
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="py-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-[640px] mx-auto mb-14">
            <span className="eyebrow">{c.steps_section?.eyebrow || "Four steps · ten minutes"}</span>
            <h2 className="text-[clamp(2rem,4vw,3rem)] mt-3 mb-3">{c.steps_section?.headline || "Peel, place, press, done"}</h2>
            <p className="text-[var(--ink-soft)]">{c.steps_section?.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(c.steps || []).map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`} className="surface p-7 relative">
                <div className="font-serif text-[3rem] font-semibold leading-none absolute -top-1 -right-1 opacity-10" style={{ color: "var(--bloom)" }}>{s.n}</div>
                <div className="text-[2rem] mb-3">{s.icon}</div>
                <h3 className="font-serif text-[1.4rem] font-semibold mb-2">{s.title}</h3>
                <p className="text-[var(--ink-soft)] text-[.95rem]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-20">
        <div className="max-w-[1180px] mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div className="rounded-[36px] p-12 relative overflow-hidden" style={{ background: "linear-gradient(160deg, var(--rose), var(--petal-2))" }}>
            <div className="font-serif text-[2.6rem] md:text-[3rem] font-semibold leading-[1.05] whitespace-pre-line" style={{ color: "var(--ink)" }}>
              &ldquo;{c.story_quote || "Pretty nails shouldn't cost a salon trip."}&rdquo;
            </div>
            <div className="float-orn" style={{ top: "10%", right: "8%", transform: "scale(.6)" }}><div className="petal-shape" /></div>
            <div className="float-orn" style={{ bottom: "18%", right: "20%", animationDelay: "1.4s", fontSize: "1.4rem" }}>✦</div>
          </div>
          <div>
            <span className="eyebrow">{c.story.eyebrow}</span>
            <h2 className="text-[clamp(2rem,4vw,2.8rem)] mt-3 mb-5">{c.story.headline_line1} <em>{c.story.headline_italic}</em> {c.story.headline_line2}</h2>
            <p className="text-[var(--ink-soft)] mb-4 leading-relaxed">{c.story.body1}</p>
            <p className="text-[var(--ink-soft)] mb-6 leading-relaxed">{c.story.body2}</p>
            <p className="serif-italic text-[var(--ink-soft)]">{c.story_sign || "— with love"}</p>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-20" style={{ background: "var(--petal-2)" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-[640px] mx-auto mb-12">
            <span className="eyebrow">{c.testimonials_eyebrow}</span>
            <h2 className="text-[clamp(2rem,4vw,3rem)] mt-3 mb-3">{c.testimonials_headline}</h2>
            <p className="text-[var(--ink-soft)]">{c.testimonials_subhead}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {(c.testimonials || []).slice(0, 3).map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="surface p-7">
                <div className="rating-stars text-[1.05rem]">★★★★★</div>
                <p className="my-4 text-[var(--ink)] leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white" style={{ background: "linear-gradient(135deg, var(--bloom), var(--punch))" }}>
                    {t.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <b className="text-[var(--ink)]">{t.name}</b><br />
                    <small className="text-[var(--ink-soft)] text-xs">{t.city}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUIZ */}
      <section className="pb-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="rounded-[36px] p-10 md:p-16 text-center relative overflow-hidden" style={{ background: "linear-gradient(160deg, #fff, var(--petal-2))" }}>
            <span className="eyebrow">{c.quiz.eyebrow}</span>
            <h2 className="text-[clamp(2rem,4vw,2.8rem)] mt-3 mb-3">{c.quiz.headline_line1}<br />{c.quiz.headline_line2}</h2>
            <p className="text-[var(--ink-soft)] max-w-[480px] mx-auto mb-9">{c.quiz.subtitle}</p>
            <div className="flex flex-wrap justify-center gap-4">
              {(c.quiz.options || []).map((s) => (
                <button key={s.id} onClick={() => setShape(s)} data-testid={`shape-${s.id}`}
                  className={`rounded-[22px] px-6 py-5 w-32 transition-all ${shape?.id === s.id ? "shadow-[0_14px_30px_-12px_rgba(224,53,107,.55)] -translate-y-1" : "hover:-translate-y-1"}`}
                  style={{ background: shape?.id === s.id ? "linear-gradient(135deg, var(--bloom), var(--punch))" : "#fff", color: shape?.id === s.id ? "#fff" : "var(--ink)" }}>
                  <div className="text-3xl mb-1">{s.emoji}</div>
                  <div className="font-serif text-lg font-semibold">{s.label}</div>
                </button>
              ))}
            </div>
            {shape && (
              <div data-testid="shape-result" className="mt-10 inline-flex flex-col items-center gap-4 surface px-8 py-7 max-w-md mx-auto">
                <span className="eyebrow">{c.quiz.result_eyebrow}</span>
                <div className="font-serif text-[1.5rem] font-semibold">{shape.desc}</div>
                <button onClick={() => navigate(`/shop?category=${shape.cat}`)} data-testid="shape-result-cta" className="btn btn-primary">
                  Show me the {shape.cat} collection <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF (UGC gallery) */}
      {c.social_proof?.enabled && (c.social_proof?.items || []).length > 0 && (
        <SocialProofSection block={c.social_proof} />
      )}
    </div>
  );
}

function FeaturedCarousel({ products, emptyMessage, seeAllHref = "/shop" }) {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [products.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-card]");
    const step = card ? card.clientWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!products.length) {
    return <div className="text-center text-[var(--ink-soft)] py-10">{emptyMessage || "No featured picks yet — check the shop for the full collection."}</div>;
  }

  return (
    <div className="relative" data-testid="featured-carousel">
      {/* edge fades on desktop */}
      <div className="pointer-events-none hidden md:block absolute inset-y-0 left-0 w-10 z-[2]" style={{ background: "linear-gradient(90deg, var(--petal), transparent)" }} />
      <div className="pointer-events-none hidden md:block absolute inset-y-0 right-0 w-10 z-[2]" style={{ background: "linear-gradient(270deg, var(--petal), transparent)" }} />

      {/* prev/next buttons (desktop only) */}
      <button
        type="button"
        onClick={() => scrollBy(-1)}
        disabled={!canPrev}
        aria-label="Previous"
        data-testid="carousel-prev"
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full items-center justify-center z-[3] bg-white border border-[#fde0e8] shadow-sm hover:border-[var(--bloom)] disabled:opacity-40 disabled:hover:border-[#fde0e8] transition"
      >
        <ChevronLeft className="w-5 h-5" style={{ color: "var(--bloom)" }} />
      </button>
      <button
        type="button"
        onClick={() => scrollBy(1)}
        disabled={!canNext}
        aria-label="Next"
        data-testid="carousel-next"
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-11 h-11 rounded-full items-center justify-center z-[3] bg-white border border-[#fde0e8] shadow-sm hover:border-[var(--bloom)] disabled:opacity-40 disabled:hover:border-[#fde0e8] transition"
      >
        <ChevronRight className="w-5 h-5" style={{ color: "var(--bloom)" }} />
      </button>

      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-6 px-6 md:mx-0 md:px-0 scrollbar-hide"
      >
        {products.map((p) => (
          <Link
            key={p.id}
            to={`/product/${p.slug}`}
            data-testid={`featured-product-${p.slug}`}
            data-carousel-card
            className="product-card surface block p-5 relative snap-start shrink-0 w-[76%] xs:w-[68%] sm:w-[46%] md:w-[31%] lg:w-[23.5%]"
          >
            {p.compare_at && <span className="tag-pill absolute top-4 left-4">Save {Math.round((1 - p.price / p.compare_at) * 100)}%</span>}
            {p.featured && !p.compare_at && <span className="tag-pill new absolute top-4 left-4">New</span>}
            {p.stock === 0 && <span className="tag-pill sold absolute top-4 left-4">Sold out</span>}

            <div className="aspect-square rounded-2xl overflow-hidden mb-4" style={{ background: "linear-gradient(135deg, var(--rose), #fff)" }}>
              <img src={productImg(p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <h3 className="font-serif text-[1.15rem] sm:text-[1.25rem] font-semibold mb-1 leading-tight">{p.name}</h3>
            <div className="text-[.82rem] mb-2">
              <Stars5 rating={p.avg_rating || 5} />
              <span className="text-[var(--ink-soft)] ml-1">({p.review_count || 0})</span>
            </div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-serif text-[1.2rem] font-semibold" style={{ color: "var(--ink)" }}>{formatINR(p.price)}</span>
              {p.compare_at && <span className="text-sm text-[var(--ink-soft)] line-through">{formatINR(p.compare_at)}</span>}
            </div>
            <span className="btn btn-primary w-full justify-center text-sm">View pack</span>
          </Link>
        ))}
      </div>

      {/* mobile hint dots */}
      <div className="flex md:hidden justify-center gap-1.5 mt-1">
        {products.map((_, i) => (
          <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: "rgba(224,53,107,.22)" }} />
        ))}
      </div>

      <div className="text-center mt-8">
        <Link to={seeAllHref} data-testid="see-all-cta" className="btn btn-ghost text-sm">
          See the full collection <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function SocialProofSection({ block }) {
  const items = block?.items || [];
  return (
    <section className="py-20" id="social-proof">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="text-center max-w-[640px] mx-auto mb-10">
          {block.eyebrow && <span className="eyebrow">{block.eyebrow}</span>}
          <h2 className="text-[clamp(2rem,4vw,3rem)] mt-3 mb-3">
            {block.headline_part1} <em style={{ color: "var(--bloom)" }}>{block.headline_italic}</em>
          </h2>
          {block.subtitle && <p className="text-[var(--ink-soft)]">{block.subtitle}</p>}
        </div>
        <div
          data-testid="social-proof-grid"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
        >
          {items.map((it, i) => (
            <SocialTile key={i} item={it} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SocialTile({ item }) {
  const isVideo = (item.type || "").toLowerCase() === "video";
  const url = productImg(item.url);
  return (
    <div className="relative aspect-square rounded-2xl overflow-hidden bg-white group border border-[#fde0e8]">
      {isVideo ? (
        <video
          src={url}
          poster={item.poster ? productImg(item.poster) : undefined}
          muted
          loop
          playsInline
          autoPlay
          className="w-full h-full object-cover"
        />
      ) : (
        <img src={url} alt={item.author || ""} className="w-full h-full object-cover" loading="lazy" />
      )}
      {item.author && (
        <div className="absolute inset-x-0 bottom-0 p-2.5 text-white text-xs font-bold tracking-wide opacity-0 group-hover:opacity-100 transition"
             style={{ background: "linear-gradient(0deg, rgba(0,0,0,.55), transparent)" }}>
          {item.author}
        </div>
      )}
      {isVideo && (
        <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-black/50 text-white">
          Reel
        </span>
      )}
    </div>
  );
}

